import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { LogIn, UserPlus, Mail, Lock, AlertCircle, Gift, User, Fingerprint } from 'lucide-react'
import { APP_VERSION } from './Header'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Logo } from './Logo'
import { supabase } from '../lib/supabase'
import { useTranslation } from 'react-i18next'
import { Capacitor } from '@capacitor/core'
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth'
import ConfirmDialog from './ConfirmDialog'

export default function LoginForm() {
  const { signIn, signUp, signInWithGoogle } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [nickname, setNickname] = useState('')
  const [referralCode, setReferralCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [biometricAvailable, setBiometricAvailable] = useState(false)
  const [hasSavedCredentials, setHasSavedCredentials] = useState(false)
  const [showBiometricConfirm, setShowBiometricConfirm] = useState(false)
  const [pendingCredentials, setPendingCredentials] = useState<{ email: string; password: string } | null>(null)

  // Check if registration is enabled via environment variable
  const registrationEnabled = import.meta.env.VITE_REGISTRATION_ENABLED !== 'false'

  useEffect(() => {
    const ref = searchParams.get('ref')
    const signup = searchParams.get('signup')

    // Only allow signup mode if registration is enabled
    if (!registrationEnabled) {
      setError('現在、新規登録を一時的に停止しております。再開まで今しばらくお待ちください。')
      return
    }

    // チーム招待から来た場合、localStorageから紹介コードを読み取る
    const pendingReferralCode = localStorage.getItem('pendingReferralCode')

    if (ref) {
      // Keep original case for slugs (e.g., "john-doe"), uppercase only for numeric codes
      setReferralCode(ref)
      setIsSignUp(true)
    } else if (pendingReferralCode) {
      // チーム招待からの紹介コードを適用
      setReferralCode(pendingReferralCode)
      setIsSignUp(true)
    } else if (signup === 'true') {
      // Open in signup mode when signup parameter is present
      setIsSignUp(true)
    }
  }, [searchParams, registrationEnabled])

  // Extract referral code from URL if full URL is pasted
  const extractReferralCode = (input: string): string => {
    // If input contains URL pattern, extract code from ref parameter
    const urlMatch = input.match(/[?&]ref=([^&]+)/i)
    if (urlMatch) {
      return urlMatch[1] // Keep original case for slugs
    }
    // Otherwise return as-is (could be code or slug)
    return input
  }

  // 生体認証の利用可能性をチェック
  useEffect(() => {
    const checkBiometric = async () => {
      if (!Capacitor.isNativePlatform()) {
        return
      }

      try {
        const result = await BiometricAuth.checkBiometry()
        setBiometricAvailable(result.isAvailable)

        // 保存された認証情報があるかチェック
        const savedEmail = localStorage.getItem('biometric_email')
        setHasSavedCredentials(!!savedEmail)
        if (savedEmail) {
          setEmail(savedEmail)
        }
      } catch (error) {
        console.error('Biometric check error:', error)
      }
    }

    checkBiometric()
  }, [])

  // 生体認証でログイン
  const handleBiometricLogin = async () => {
    if (!biometricAvailable || !hasSavedCredentials) {
      setError('生体認証が利用できません')
      return
    }

    try {
      setLoading(true)
      setError('')

      const savedEmail = localStorage.getItem('biometric_email')
      const savedPassword = localStorage.getItem('biometric_password')

      if (!savedEmail || !savedPassword) {
        setError('保存された認証情報が見つかりません')
        return
      }

      // 生体認証を実行
      await BiometricAuth.authenticate({
        reason: 'ログインするために認証してください',
        cancelTitle: 'キャンセル',
        iosFallbackTitle: 'パスコードを使用',
        androidTitle: '生体認証',
        androidSubtitle: 'ログイン',
        androidConfirmationRequired: false
      })

      // 認証成功 → ログイン
      await signIn(savedEmail, savedPassword)

      // Check for redirect parameter
      const redirectUrl = searchParams.get('redirect')
      if (redirectUrl) {
        navigate(decodeURIComponent(redirectUrl))
      } else {
        navigate('/')
      }
    } catch (err: any) {
      console.error('Biometric login error:', err)
      if (err.code !== 10) { // ユーザーキャンセルの場合はエラー表示しない
        setError('生体認証に失敗しました')
      }
    } finally {
      setLoading(false)
    }
  }

  // 生体認証設定の確認ハンドラー
  const handleBiometricConfirm = () => {
    if (pendingCredentials) {
      // 認証情報を保存
      localStorage.setItem('biometric_email', pendingCredentials.email)
      localStorage.setItem('biometric_password', pendingCredentials.password)
      setHasSavedCredentials(true)
    }
    setShowBiometricConfirm(false)
    setPendingCredentials(null)

    // ナビゲーションを実行
    const redirectUrl = searchParams.get('redirect')
    if (redirectUrl) {
      navigate(decodeURIComponent(redirectUrl))
    } else {
      navigate('/')
    }
  }

  // 生体認証設定をスキップするハンドラー
  const handleBiometricCancel = () => {
    setShowBiometricConfirm(false)
    setPendingCredentials(null)

    // ナビゲーションを実行
    const redirectUrl = searchParams.get('redirect')
    if (redirectUrl) {
      navigate(decodeURIComponent(redirectUrl))
    } else {
      navigate('/')
    }
  }

  const handlePasswordReset = async () => {
    if (!email) {
      setError(t('login.errors.emailRequired'))
      return
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (error) {
        console.error('Password reset error:', error)
        setError(t('login.errors.resetFailed'))
      } else {
        setResetEmailSent(true)
        setShowPasswordReset(false)
      }
    } catch (err) {
      console.error('Unexpected password reset error:', err)
      setError(t('login.errors.unexpected'))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setMessage('')

    if (isSignUp && password !== confirmPassword) {
      setError(t('login.errors.passwordMismatch'))
      return
    }

    if (password.length < 6) {
      setError(t('login.errors.passwordLength'))
      return
    }

    if (isSignUp && !nickname) {
      setError('ニックネームを入力してください')
      return
    }

    if (isSignUp && (nickname.length < 3 || nickname.length > 30)) {
      setError('ニックネームは3文字以上30文字以内で入力してください')
      return
    }

    if (isSignUp && !agreedToTerms) {
      setError(t('login.errors.termsRequired'))
      return
    }

    setLoading(true)

    try {
      if (isSignUp) {
        // Extract and validate referral code
        const cleanCode = referralCode ? extractReferralCode(referralCode) : ''

        // Get redirect URL if present (e.g., for team invitations)
        const redirectUrl = searchParams.get('redirect')
        const emailRedirectTo = redirectUrl
          ? `${window.location.origin}${decodeURIComponent(redirectUrl)}`
          : `${window.location.origin}/`

        // Pass referral code and nickname as metadata - it will be saved by handle_new_user trigger
        const metadata = {
          nickname,
          ...(cleanCode ? { referral_code: cleanCode } : {})
        }
        await signUp(email, password, metadata, emailRedirectTo)

        // 登録成功後、localStorageの紹介情報をクリア
        localStorage.removeItem('pendingReferralCode')
        localStorage.removeItem('pendingReferralEmail')
        localStorage.removeItem('pendingTeamInvitation')

        // Show appropriate message based on whether there's a redirect
        if (redirectUrl) {
          setMessage('確認メールを送信しました。メールのリンクをクリックして確認後、招待ページに戻ります。')
        } else {
          setMessage(t('login.messages.confirmationSent'))
        }
        setIsSignUp(false)
        setEmail('')
        setPassword('')
        setConfirmPassword('')
        setNickname('')
        setReferralCode('')
      } else {
        await signIn(email, password)

        // ログイン成功時、ネイティブアプリで生体認証が利用可能なら認証情報を保存するか確認
        if (Capacitor.isNativePlatform() && biometricAvailable && !hasSavedCredentials) {
          // 認証情報を一時保存して確認ダイアログを表示
          setPendingCredentials({ email, password })
          setShowBiometricConfirm(true)
          return // ダイアログの結果を待つため、ここでナビゲーションを止める
        }

        // Check for redirect parameter
        const redirectUrl = searchParams.get('redirect')
        if (redirectUrl) {
          // Decode and navigate to the redirect URL
          navigate(decodeURIComponent(redirectUrl))
        } else {
          navigate('/')
        }
      }
    } catch (err: any) {
      if (err.message.includes('Invalid login credentials')) {
        setError(t('login.errors.invalidCredentials'))
      } else if (err.message.includes('User already registered')) {
        setError(t('login.errors.userExists'))
      } else if (err.message.includes('duplicate key value violates unique constraint "unique_nickname"')) {
        setError('このニックネームは既に使用されています')
      } else {
        setError(err.message || t('login.errors.unexpected'))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center">
            <a
              href="https://tastack.net"
              className="inline-block transition-opacity hover:opacity-80 cursor-pointer"
              onClick={(e) => {
                e.preventDefault()
                window.location.href = 'https://tastack.net'
              }}
            >
              <Logo noLink />
            </a>
          </div>
          <p className="mt-6 text-center text-sm text-gray-600">
            {isSignUp ? t('login.signupTitle') : t('login.title')}
          </p>
        </div>
        
        <form 
          className="mt-8 space-y-6" 
          onSubmit={handleSubmit}
          autoComplete="on"
          method="POST"
        >
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                {t('login.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-none relative block w-full pl-10 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                  placeholder={t('login.email')}
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="sr-only">
                {t('login.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete={isSignUp ? "new-password" : "current-password"}
                  autoCapitalize="off"
                  autoCorrect="off"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`appearance-none rounded-none relative block w-full pl-10 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 ${
                    !isSignUp ? 'rounded-b-md' : ''
                  } focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder={t('login.password')}
                />
              </div>
              {!isSignUp && (
                <div className="text-right mt-2">
                  <button
                    type="button"
                    onClick={() => setShowPasswordReset(true)}
                    className="text-xs text-indigo-600 hover:text-indigo-700"
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>
              )}
            </div>

            {isSignUp && (
              <>
                <div>
                  <label htmlFor="confirmPassword" className="sr-only">
                    {t('login.confirmPassword')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="appearance-none rounded-none relative block w-full pl-10 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                      placeholder={t('login.confirmPassword')}
                    />
                </div>
              </div>
              <div>
                <label htmlFor="nickname" className="sr-only">
                  ニックネーム
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    autoComplete="off"
                    required
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    className="appearance-none rounded-none relative block w-full pl-10 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                    placeholder="ニックネーム（3-30文字）"
                    minLength={3}
                    maxLength={30}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="referralCode" className="sr-only">
                  {t('login.referralCode')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Gift className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="referralCode"
                    name="referralCode"
                    type="text"
                    autoComplete="off"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    className="appearance-none rounded-none relative block w-full pl-10 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                    placeholder={t('login.referralCode')}
                  />
                </div>
                {referralCode && (
                  <p className="mt-1 text-xs text-green-600">
                    {t('login.referralApplied')}
                  </p>
                )}
              </div>
            </>
            )}
          </div>

          {isSignUp && (
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="text-gray-700 cursor-pointer">
                  <a
                    href="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-500 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('login.termsOfService')}
                  </a>
                  {' '}{t('login.and')}{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-500 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t('login.privacyPolicy')}
                  </a>
                  {t('login.agreeToTerms')}
                </label>
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {message && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm text-green-800">{message}</p>
                </div>
              </div>
            </div>
          )}

          {resetEmailSent && (
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <p className="text-sm text-green-800">{t('login.messages.resetSent')}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              name="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                {isSignUp ? (
                  <UserPlus className="h-5 w-5 text-indigo-500 group-hover:text-indigo-400" />
                ) : (
                  <LogIn className="h-5 w-5 text-indigo-500 group-hover:text-indigo-400" />
                )}
              </span>
              {loading ? t('login.processing') : isSignUp ? t('login.signUp') : t('login.signIn')}
            </button>

            {/* Biometric Login Button (Only on native apps, login mode, and with saved credentials) */}
            {!isSignUp && biometricAvailable && hasSavedCredentials && Capacitor.isNativePlatform() && (
              <button
                type="button"
                onClick={handleBiometricLogin}
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <Fingerprint className="h-5 w-5 text-gray-500 group-hover:text-gray-600" />
                </span>
                生体認証でログイン
              </button>
            )}

            {/* Hide Google Sign-in when registration is disabled */}
            {registrationEnabled && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">{t('login.or')}</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setError('')
                      await signInWithGoogle()
                      // ログイン成功後、ダッシュボードに遷移
                      navigate('/')
                    } catch (err: any) {
                      // キャンセル以外のエラーを表示
                      if (err.message && !err.message.includes('canceled')) {
                        setError(err.message)
                      }
                    }
                  }}
                  className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
{t('login.signInWithGoogle')}
                </button>
              </>
            )}
          </div>

          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => {
                // Check if trying to switch to sign up mode
                if (!isSignUp && !registrationEnabled) {
                  setError('現在、新規登録を一時的に停止しております。再開まで今しばらくお待ちください。')
                  return
                }
                setIsSignUp(!isSignUp)
                setError('')
                setMessage('')
                setPassword('')
                setConfirmPassword('')
                setNickname('')
                setAgreedToTerms(false)
              }}
              className="text-sm text-indigo-600 hover:text-indigo-500"
            >
              {isSignUp ? t('login.haveAccount') : t('login.noAccount')}
            </button>
          </div>
        </form>

        {/* Password Reset Modal */}
        {showPasswordReset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('login.passwordReset.title')}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t('login.passwordReset.description', { email: email || t('login.email') })}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPasswordReset(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  {t('login.passwordReset.cancel')}
                </button>
                <button
                  onClick={handlePasswordReset}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  {t('login.passwordReset.send')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* バージョン表示 */}
        <div className="text-center mt-8">
          <span className="text-xs text-gray-400">v{APP_VERSION}</span>
        </div>
      </div>

      {/* 生体認証設定確認ダイアログ */}
      <ConfirmDialog
        isOpen={showBiometricConfirm}
        title="生体認証の設定"
        message="次回から生体認証でログインしますか？"
        confirmText="設定する"
        cancelText="スキップ"
        variant="info"
        onConfirm={handleBiometricConfirm}
        onCancel={handleBiometricCancel}
      />
    </div>
  )
}